import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { SELECTION_LIMIT } from '@/constants';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/services/auth';
import { breedByIdOptions, breedsOptions } from '@/services/lookups';
import { BreedResponse } from '@/services/lookups/types';

type BreedsComboboxProps = {
  id?: string;
  value?: string;
  onChange?: (breed: BreedResponse | undefined) => void;
  subFamilies?: number[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  invalid?: boolean;
  tabIndex?: number;
  container?: HTMLElement;
};

export const BreedsCombobox = ({
  id,
  value,
  onChange,
  subFamilies,
  placeholder,
  className,
  disabled,
  invalid,
  tabIndex,
  container,
}: BreedsComboboxProps) => {
  const { t } = useTranslation();
  const [typedSearch, setTypedSearch] = useState<string | null>(null);
  const debouncedSearch = useDebounce(typedSearch ?? '');
  const token = useAccessToken();

  const { data: breeds } = useQuery({
    ...breedsOptions(token, { search: debouncedSearch, subFamilies }),
    enabled: !!token,
    select: (data) => data.slice(0, SELECTION_LIMIT),
  });
  const { data: initialBreed } = useQuery({
    ...breedByIdOptions(token, value ?? ''),
    enabled: !!token && !!value,
  });

  const selectedBreed = value
    ? (breeds?.find((b) => b.id === value) ?? initialBreed ?? null)
    : null;

  const breedSearch = typedSearch ?? selectedBreed?.name ?? '';

  const handleBreedChange = (breed: BreedResponse | null) => {
    setTypedSearch(null);
    onChange?.(breed ?? undefined);
  };

  return (
    <Combobox
      items={breeds ?? []}
      value={selectedBreed}
      onValueChange={handleBreedChange}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
      filter={null}
      onOpenChange={(open) => {
        if (!open) {
          setTypedSearch(null);
        }
      }}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder ?? t('fields.selectBreed')}
        className={cn('w-full', className)}
        value={breedSearch}
        onChange={(e) => {
          setTypedSearch(e.target.value);
        }}
        disabled={disabled}
        aria-invalid={invalid}
        tabIndex={tabIndex}
        showClear
      />
      <ComboboxContent container={container}>
        <ComboboxEmpty>{t('fields.noBreedsFound')}</ComboboxEmpty>
        <ComboboxList>
          {(breed: BreedResponse) => (
            <ComboboxItem key={breed.id} value={breed}>
              {breed.name} [{breed.code}]
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
